<template>
    <wrapper-features-templates-operations
    nonAuthenticatedFeature
    :customBreadcrumbs="customBreadcrumbs"
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState"
    :stepperParams="stepperParams"
    :activeStep="activeStep"
    :showStepper="!showLoader && !applyErrorState"
    :showFooter="!showLoader && !applyErrorState"
    :footerButtonsParams="footerButtonsParams.filter(ele => !ele.hidden)"
    :buttonFullWidth="activeStep === 4"
    :scrollValue="scrollValue"
    @backOrCloseClicked="navigateByName(routeList.LOGIN_NAME, { showRegularLogin: true })">
        <!-- Insert content -->
        <component
        v-if="!showLoader && !applyErrorState"
        :is="getActiveStepComponent"
        :formData="formData"
        :mandatoryList="mandatoryList[`step${activeStep}MandatoryList`]"
        :showMandatoryErrors="showMandatoryErrors"
        :clearFormErrors="clearFormErrors"
        @formDataChange="formDataChangeHandler"
        @formErrorsCleared="clearFormErrors = false"
        @clearStep3="clearStep3"
        @addStepValidationError="addStepValidationError"
        @removeStepValidationError="removeStepValidationError"
        @resetShowMandatoryErrorsProp="showMandatoryErrors = false"
        @scrollTo="scrollValue = $event"
        @resendCredentials="resendCredentialsHandler"/>
    </wrapper-features-templates-operations>

    <modal-centered-token
    v-if="showTokenModal"
    :titleFull="t('recoverCredentials.tokenModal.title')"
    :modalCustomUseParams="{
        messageToDisplay: t('recoverCredentials.tokenModal.message'),
        sendTokenAction: 'recoverCredentials/sendValidatePhoneNumberOTP',
        resendTokenAction: 'recoverCredentials/resendOTP',
        payload: { requestToken: phoneToken },
        resendPayload: { token: phoneToken }
    }"
    @challengeSuccessHandler="challengeSuccessHandler"
    @challengeErrorHandler="[errorHandler($event, validateStep1Inputs), showTokenModal = false]"/>

    <!-- Feedback Toasts -->
    <toasts-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import ModalCenteredToken from '@/components/generic/modals/centered/Token'
import ToastsGeneric from '@/components/generic/toasts/Generic'

/* Vue  */
import { ref, computed, defineAsyncComponent } from 'vue'

/* Assets & Helpers */
import { atLeastOneIsEmpty } from '@/assets/scripts/objects'
import { errorObjectConstructor, emptyObjectConstructor, sucessObjectConstrutor } from '@/assets/scripts/toastsHelpers'
import { navigateByPath, navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'

/* Store */
import { mapActions } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { validatePhoneNumber, validatePersonalData, retrieveCredentials, retrieveCredentialsResend } = mapActions(moduleMapper.RECOVER_CREDENTIALS)

/* Generic Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const applyErrorState = ref(false)
const showTokenModal = ref(false)

const customBreadcrumbs = {
    currentArea: t('recoverCredentials.customBreadcrumbs.currentArea')
}

/*************************************************
*                                                *
*                    Steps Data                  *
*                                                *
*************************************************/
const activeStep = ref(1)
const stepperParams = ref([
    { description: t('recoverCredentials.steps.phone') },
    { description: t('recoverCredentials.steps.data') },
    { description: t('recoverCredentials.steps.credentials') },
    { description: t('recoverCredentials.steps.informations') }
])

const getActiveStepComponent = computed(() => {
    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/nonAuthenticated/recoverCredentials/Step' + stepNumber))
})

const goToStep = stepNumber => {
    activeStep.value = stepNumber
    scrollValue.value = 0
}

/*************************************************
*                                                *
*                Form Logic Data                 *
*                                                *
*************************************************/
const scrollValue = ref(null)
const showMandatoryErrors = ref(false)
const clearFormErrors = ref(false)

const formData = ref({
    phoneNumber: '',
    documentNumber: '',
    birthdayDate: '',
    selectedTab: 'accountNumber',
    accountNumber: '',
    customerNumber: '',
    selectedRadio: 'acession',
    newPassword: '',
    newPasswordConfirmation: ''
})

const step1MandatoryList = ref([
    'phoneNumber'
])

const step2MandatoryList = ref([
    'documentNumber',
    'birthdayDate'
])

const mandatoryList = ref({
    step1MandatoryList: step1MandatoryList.value,
    step2MandatoryList: step2MandatoryList.value
})

let stepValidationErrors = []

const addStepValidationError = param => {
    if (!stepValidationErrors.find(ele => ele === param)) stepValidationErrors.push(param)
}

const removeStepValidationError = param => {
    if (stepValidationErrors.find(ele => ele === param)) stepValidationErrors.splice(stepValidationErrors.findIndex(ele => ele === param), 1)
}

const formDataChangeHandler = ({ field, inputValue }) => {
    formData.value[field] = inputValue

    if (field === 'selectedRadio') clearFormErrors.value = true

    formData.value.selectedTab === 'customerNumber' ? formData.value.accountNumber = '' : formData.value.customerNumber = ''
}

/*************************************************
 *                                                *
 *               Footer/Buttons Logic             *
 *                                                *
 *************************************************/
const clearHandler = () => {
    clearFormErrors.value = true
    stepValidationErrors = []

    clearList.value[`clearStep${activeStep.value}`]()
}

const validateStepsHandler = () => {
    validationsList.value[`validateStep${activeStep.value}`]()
}

const clearStep1 = () => {
    formData.value.phoneNumber = ''
}

const clearStep2 = () => {
    formData.value.documentNumber = ''
    formData.value.birthdayDate = ''
    formData.value.selectedTab = 'accountNumber'
    formData.value.accountNumber = ''
    formData.value.customerNumber = ''
}

const clearStep3 = () => {
    formData.value.newPassword = ''
    formData.value.newPasswordConfirmation = ''
}

const phoneToken = ref('')
const flowToken = ref('')
const validateStep1Inputs = async () => {
    showTokenModal.value = false

    if (stepValidationErrors.length) return

    if (atLeastOneIsEmpty(formData.value, mandatoryList.value[`step${activeStep.value}MandatoryList`])) {
        showMandatoryErrors.value = true
        return
    }

    showOverlayLoader.value = true

    try {
        const res = await validatePhoneNumber({
            phoneNumber: formData.value.phoneNumber
        })

        if (res.data.data.token) {
            phoneToken.value = res.data.data.token
            showTokenModal.value = true
        }

        showOverlayLoader.value = false
    } catch (error) {
        errorHandler(error.response.data, validateStep1Inputs, () => { showFeedbackToast.value = false })
    }
}

const validateStep2Inputs = async () => {
    if (stepValidationErrors.length) return

    if (atLeastOneIsEmpty(formData.value, mandatoryList.value[`step${activeStep.value}MandatoryList`])) {
        showMandatoryErrors.value = true
        return
    }

    if ((formData.value.selectedTab === 'accountNumber' && !formData.value.accountNumber) || (formData.value.selectedTab === 'customerNumber' && !formData.value.customerNumber)) {
        showMandatoryErrors.value = true
        return
    }

    try {
        showOverlayLoader.value = true

        await validatePersonalData({
            requestToken: flowToken.value,
            documentNumber: formData.value.documentNumber,
            birthdayDate: formData.value.birthdayDate,
            accountNumber: formData.value.selectedTab === 'accountNumber' ? formData.value.accountNumber : null,
            customerNumber: formData.value.selectedTab === 'customerNumber' ? formData.value.customerNumber : null
        })

        goToStep(3)
        showOverlayLoader.value = false
    } catch (error) {
        errorHandler(error.response.data, validateStep2Inputs, () => { showFeedbackToast.value = false })
    }
}

const types = ref({
    acession: 'ACCESSION_NUMBER',
    password: 'NEW_PASSWORD',
    acessionAndPassword: 'ACCESSION_AND_NEW_PASSWORD'
})

const validateStep3Inputs = async () => {
    if (stepValidationErrors.length) return

    if (formData.value.selectedRadio !== 'acession' && (!formData.value.newPassword || !formData.value.newPasswordConfirmation)) {
        showMandatoryErrors.value = true
        return
    }

	if (formData.value.selectedRadio !== 'acession' && (formData.value.newPassword !== formData.value.newPasswordConfirmation)) { return }

    try {
        showOverlayLoader.value = true

        await retrieveCredentials({
            requestToken: flowToken.value,
            type: types.value[formData.value.selectedRadio],
            newPassword: formData.value.selectedRadio !== 'acession' ? formData.value.newPassword : null,
            newPasswordConfirmation: formData.value.selectedRadio !== 'acession' ? formData.value.newPasswordConfirmation : null
        })

        successHandler(formData.value.selectedRadio === 'password' ? t('recoverCredentials.passwordSuccessMessage') : t('recoverCredentials.credentialsSuccessMessage'))

        goToStep(4)
        showOverlayLoader.value = false
    } catch (error) {
        errorHandler(error.response.data, validateStep3Inputs, () => { showFeedbackToast.value = false })
    }
}

const resendCredentialsHandler = async () => {
    showOverlayLoader.value = true

    try {
        await retrieveCredentialsResend({
            token: flowToken.value
        })

		successHandler(formData.value.selectedRadio === 'password' ? t('recoverCredentials.passwordSuccessMessage') : t('recoverCredentials.credentialsSuccessMessage'))

        showOverlayLoader.value = false
    } catch (error) {
        errorHandler(error.response.data, resendCredentialsHandler, () => { showFeedbackToast.value = false })
    }
}

const clearList = ref({
    clearStep1: clearStep1,
    clearStep2: clearStep2,
    clearStep3: clearStep3
})

const validationsList = ref({
    validateStep1: validateStep1Inputs,
    validateStep2: validateStep2Inputs,
    validateStep3: validateStep3Inputs
})

const footerButtonsParams = computed(() => {
    return [{
        label: t('approvals.operators.create.footer.clear'),
        buttonPosition: 'left',
        hidden: activeStep.value === 4,
        callbackFunction: clearHandler
    }, {
        label: t('approvals.operators.create.footer.confirm'),
        icon: 'arrow-right-full',
        buttonType: 'primary',
        iconPosition: 'right',
        hidden: activeStep.value === 4,
        callbackFunction: validateStepsHandler
    }, {
        label: t('login.loginBtn'),
		icon: 'arrow-right-full',
        iconPosition: 'right',
        buttonType: 'primary',
        hidden: activeStep.value !== 4,
        callbackFunction: () => navigateByPath(routeList.LOGIN_PATH)
    }]
})

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const challengeSuccessHandler = data => {
    flowToken.value = data.data.token
    showTokenModal.value = false
    goToStep(2)
}

const successHandler = data => {
    feedbackToastData.value = sucessObjectConstrutor(data)
	showFeedbackToast.value = true
}

const errorHandler = (data, retryFn, resolveBtnFunction) => {
    showLoader.value = false
    showOverlayLoader.value = false

    if (data.code === 'retrieve_credentials_max_attempts_reached') {
        feedbackToastData.value = errorObjectConstructor({
            message: data.message,
            dismissToastBtnText: t('login.errorFeedbackToast')
        })
    } else {
        feedbackToastData.value = errorObjectConstructor(data, retryFn, resolveBtnFunction)
    }
	showFeedbackToast.value = true
}
</script>

<style lang="scss" scoped>
</style>
